import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import StoreButtons from './StoreButtons';
import { useTranslation } from '../i18n/LanguageContext';

const languages = [
  { code: 'tr', label: 'Türkçe' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
];

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const { t, language, changeLanguage } = useTranslation();

  const navLinks = [
    { label: t('nav.about'), path: '/about' },
    { label: t('nav.privacy'), path: '/privacy' },
    { label: t('nav.terms'), path: '/terms' },
    { label: t('nav.contact'), path: '/contact' },
  ];

  return (
    <header
      style={{
        backgroundColor: '#FEFFFC',
        borderBottom: '1px solid #E7EAE5',
      }}
      className="sticky top-0 z-50"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-3">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <motion.img
              src="/logo/derkenar-logo-dark.svg"
              alt="Derkenar Logo"
              whileHover={{ scale: 1.05 }}
              style={{
                height: '40px',
                width: 'auto',
              }}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex gap-8 items-center">
            {navLinks.map((link, index) => (
              <Link
                key={index}
                to={link.path}
                className="font-filson font-medium text-gray-700 hover:text-primary transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Language Selector Dropdown */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setIsLanguageOpen(!isLanguageOpen)}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #CB4B4B',
                backgroundColor: '#FEFFFC',
                color: '#CB4B4B',
                fontFamily: "'Filson Soft', sans-serif",
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.3s',
              }}
            >
              {languages.find(l => l.code === language)?.label}
              <span style={{ fontSize: '10px' }}>▼</span>
            </button>
            {isLanguageOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '4px',
                  backgroundColor: 'white',
                  border: '1px solid #E7EAE5',
                  borderRadius: '6px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  zIndex: 100,
                  minWidth: '120px',
                }}
              >
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      changeLanguage(lang.code);
                      setIsLanguageOpen(false);
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '10px 12px',
                      textAlign: 'left',
                      backgroundColor: language === lang.code ? '#F5F5F5' : 'transparent',
                      color: language === lang.code ? '#CB4B4B' : '#1a1a1a',
                      border: 'none',
                      fontFamily: "'Filson Soft', sans-serif",
                      fontSize: '13px',
                      fontWeight: language === lang.code ? '600' : '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#F5F5F5';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = language === lang.code ? '#F5F5F5' : 'transparent';
                    }}
                  >
                    {lang.label}
                  </button>
                ))}
              </motion.div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden flex flex-col gap-1.5"
            style={{
              padding: '8px',
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                display: 'block',
                width: '24px',
                height: '2px',
                backgroundColor: '#CB4B4B',
                transition: 'all 0.3s',
                transform: isOpen ? 'rotate(45deg) translateY(10px)' : 'none',
              }}
            />
            <span
              style={{
                display: 'block',
                width: '24px',
                height: '2px',
                backgroundColor: '#CB4B4B',
                transition: 'all 0.3s',
                opacity: isOpen ? '0' : '1',
              }}
            />
            <span
              style={{
                display: 'block',
                width: '24px',
                height: '2px',
                backgroundColor: '#CB4B4B',
                transition: 'all 0.3s',
                transform: isOpen ? 'rotate(-45deg) translateY(-10px)' : 'none',
              }}
            />
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <motion.nav
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden mt-4 pt-4 border-t border-border-color"
          >
            <div className="flex flex-col gap-4">
              {navLinks.map((link, index) => (
                <Link
                  key={index}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className="font-filson font-medium text-gray-700 hover:text-primary py-2 transition-colors"
                >
                  {link.label}
                </Link>
              ))}

              {/* Language Selector Mobile Dropdown */}
              <div style={{ paddingTop: '12px', borderTop: '1px solid #E7EAE5' }}>
                <button
                  onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #CB4B4B',
                    backgroundColor: '#FEFFFC',
                    color: '#CB4B4B',
                    fontFamily: "'Filson Soft', sans-serif",
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'all 0.3s',
                  }}
                >
                  {languages.find(l => l.code === language)?.label}
                  <span style={{ fontSize: '10px' }}>▼</span>
                </button>
                {isLanguageOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    style={{
                      marginTop: '4px',
                      backgroundColor: 'white',
                      border: '1px solid #E7EAE5',
                      borderRadius: '6px',
                      overflow: 'hidden',
                    }}
                  >
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          changeLanguage(lang.code);
                          setIsLanguageOpen(false);
                        }}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '10px 12px',
                          textAlign: 'left',
                          backgroundColor: language === lang.code ? '#F5F5F5' : 'transparent',
                          color: language === lang.code ? '#CB4B4B' : '#1a1a1a',
                          border: 'none',
                          fontFamily: "'Filson Soft', sans-serif",
                          fontSize: '13px',
                          fontWeight: language === lang.code ? '600' : '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#F5F5F5';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = language === lang.code ? '#F5F5F5' : 'transparent';
                        }}
                      >
                        {lang.label}
                      </button>
                    ))}
                  </motion.div>
                )}
              </div>

              <div style={{ paddingTop: '8px' }}>
                <StoreButtons size="small" />
              </div>
            </div>
          </motion.nav>
        )}
      </div>
    </header>
  );
};

export default Header;
